import {
    Close as CloseIcon,
    PlayArrow as PlayIcon,
    Settings as SettingsIcon,
    Timeline as TimelineIcon,
    Build as ToolIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface Task {
  taskId: number;
  taskName: string;
  description: string;
  category: string;
  isActive: boolean;
  tools: string[];
  executionCount: number;
  successRate: number;
  lastExecutedAt: string;
  comId?: number;
}

interface TaskDetail extends Task {
  config?: {
    trigger: string;
    priority: string;
    timeout: number;
    retryCount: number;
    parameters: Array<{
      name: string;
      type: string;
      required: boolean;
    }>;
  };
  executionHistory?: Array<{
    executionId: number;
    startTime: string;
    endTime: string;
    status: string;
    duration: number;
    toolsUsed: string[];
    result?: string;
    error?: string;
  }>;
}

interface TaskDetailModalProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-detail-tabpanel-${index}`}
      aria-labelledby={`task-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ open, task, onClose }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [taskDetail, setTaskDetail] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && task) {
      loadTaskDetail();
    }
  }, [open, task]);

  const loadTaskDetail = async () => {
    if (!task) return;

    try {
      setLoading(true);
      
      // API 호출 (실제 구현에서는 apiRequest 사용)
      const response = await fetch(`/api/v1/apps/ai-manager/super-admin/tasks/${task.taskId}/detail`);
      const result = await response.json();
      
      if (result.success) {
        setTaskDetail(result.data);
      } else {
        console.error('Task detail loading failed:', result.error);
        // 실패시 기본 task 정보 사용
        setTaskDetail(task as TaskDetail);
      }
    } catch (error) {
      console.error('Task detail loading error:', error);
      // 에러시 기본 task 정보 사용
      setTaskDetail(task as TaskDetail);
    } finally {
      setLoading(false);
    }
  };

  const currentTaskData = taskDetail || task;

  if (!currentTaskData) {
    return null;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // 실제 데이터 또는 기본값 사용
  const taskConfig = (taskDetail?.config) || {
    trigger: 'user_request',
    priority: 'normal',
    timeout: 300,
    retryCount: 3,
    parameters: [
      { name: 'userId', type: 'number', required: true },
      { name: 'sessionId', type: 'string', required: true },
      { name: 'context', type: 'object', required: false }
    ]
  };

  const executionHistory = (taskDetail?.executionHistory) || [];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Task 상세 정보</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* 기본 정보 헤더 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {currentTaskData.taskName}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {currentTaskData.description}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Chip 
              label={currentTaskData.category} 
              color="primary" 
              size="small" 
            />
            <Chip 
              label={currentTaskData.isActive ? '활성' : '비활성'} 
              color={currentTaskData.isActive ? 'success' : 'default'} 
              size="small" 
            />
            <Chip 
              label={`성공률 ${currentTaskData.successRate}%`} 
              color="info" 
              size="small" 
            />
          </Box>
        </Box>

        {/* 탭 메뉴 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="기본 정보" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="사용 도구" icon={<ToolIcon />} iconPosition="start" />
            <Tab label="실행 이력" icon={<TimelineIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* 기본 정보 탭 */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Task 설정
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Task ID" 
                        secondary={currentTaskData.taskId} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="트리거 타입" 
                        secondary={taskConfig.trigger} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="우선순위" 
                        secondary={taskConfig.priority} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="타임아웃" 
                        secondary={`${taskConfig.timeout}초`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="재시도 횟수" 
                        secondary={`${taskConfig.retryCount}회`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    실행 통계
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="총 실행 횟수" 
                        secondary={`${task.executionCount}회`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="성공률" 
                        secondary={`${task.successRate}%`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="마지막 실행" 
                        secondary={new Date(task.lastExecutedAt).toLocaleString()} 
                      />
                    </ListItem>
                    {task.comId && (
                      <ListItem>
                        <ListItemText 
                          primary="도장 ID" 
                          secondary={task.comId} 
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    파라미터 정의
                  </Typography>
                  {taskConfig.parameters.map((param, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Chip 
                        label={`${param.name} (${param.type})`}
                        color={param.required ? 'error' : 'default'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {param.required && (
                        <Chip 
                          label="필수"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 사용 도구 탭 */}
        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            연결된 도구 목록
          </Typography>
          <Grid container spacing={2}>
            {task.tools.map((tool, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ToolIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">{tool}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      이 Task에서 사용되는 도구입니다.
                    </Typography>
                    <Box mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayIcon />}
                      >
                        도구 테스트
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {task.tools.length === 0 && (
            <Alert severity="info">
              이 Task에 연결된 도구가 없습니다.
            </Alert>
          )}
        </TabPanel>

        {/* 실행 이력 탭 */}
        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            최근 실행 이력
          </Typography>
                          {executionHistory.map((execution, index) => (
            <Card key={execution.executionId} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    실행 #{execution.executionId}
                  </Typography>
                  <Chip 
                    label={execution.status === 'success' ? '성공' : '실패'}
                    color={execution.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      시작: {execution.startTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      종료: {execution.endTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      소요시간: {execution.duration}ms
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>
                      사용된 도구:
                    </Typography>
                    <Box>
                      {execution.toolsUsed.map(tool => (
                        <Chip 
                          key={tool}
                          label={tool} 
                          size="small" 
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>

                {execution.result && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {execution.result}
                  </Alert>
                )}
                
                {execution.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {execution.error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
